import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { useApi } from '../../../lib/api/api-context.js';
import { parseOrThrow } from '../../../lib/api/contract.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { reservationSummaryEndpoints } from './endpoints.js';

const ZERO = 0;

const dashboardRowSchema = z.object({
  propertyId: z.string(),
  propertyName: z.string(),
  reservationCount: z.number(),
  roomNights: z.number(),
  totalFormatted: z.string(),
});

const dashboardSchema = z.object({
  totalReservations: z.number(),
  totalNights: z.number(),
  totalAmountFormatted: z.string(),
  breakdown: z.array(dashboardRowSchema),
});

function amountOf(formatted: string): number {
  const trimmed = formatted.trim();
  if (trimmed === '' || trimmed === '—' || trimmed === 'Mixed currencies') return ZERO;

  const negative = trimmed.startsWith('-') || trimmed.includes('(-');
  const amountPattern = /[\d,]+(?:\.\d+)?/;
  const match = amountPattern.exec(trimmed);
  if (match === null) return ZERO;

  const raw = match[0].replaceAll(',', '');
  const value = Number.parseFloat(raw);
  if (Number.isNaN(value)) return ZERO;
  return negative ? -value : value;
}

export interface PropertyBreakdown {
  readonly propertyId: string;
  readonly propertyName: string;
  readonly reservationCount: number;
  readonly roomNights: number;
  readonly totalAmount: number;
  readonly totalAmountFormatted: string;
  /** Share of the dashboard's total amount, from 0 to 1. */
  readonly share: number;
}

export interface ReservationSummaryShape {
  readonly totalReservations: number;
  readonly totalNights: number;
  readonly totalAmount: number;
  readonly totalAmountFormatted: string;
  readonly breakdown: readonly PropertyBreakdown[];
}

/**
 * Fetches the reservation dashboard aggregates.
 *
 * The server returns formatted totals; the hook parses those strings back to
 * numbers for chart proportions. Currency mixing is shown as the server's
 * 'Mixed currencies' label, and that property's share is shown as zero.
 */
export function useReservationDashboard(params: {
  readonly from: string;
  readonly to: string;
  readonly q: string;
}): UseQueryResult<ReservationSummaryShape> {
  const api = useApi();
  const { from, to, q } = params;

  const query: Record<string, string | undefined> = {};
  if (from !== '') query.from = from;
  if (to !== '') query.to = to;
  if (q !== '') query.q = q;

  return useQuery({
    queryKey: queryKeys.reservationSummary({ from, to, q }),
    queryFn: async () => {
      const payload = await api.get<unknown>(reservationSummaryEndpoints.dashboard, query);
      const data = parseOrThrow(dashboardSchema, payload, reservationSummaryEndpoints.dashboard);

      const breakdown: readonly PropertyBreakdown[] = data.breakdown.map((row) => ({
        propertyId: row.propertyId,
        propertyName: row.propertyName,
        reservationCount: row.reservationCount,
        roomNights: row.roomNights,
        totalAmount: amountOf(row.totalFormatted),
        totalAmountFormatted: row.totalFormatted,
        share: ZERO,
      }));

      const totalAmount = breakdown.reduce((sum, row) => sum + row.totalAmount, ZERO);

      return {
        totalReservations: data.totalReservations,
        totalNights: data.totalNights,
        totalAmount,
        totalAmountFormatted: data.totalAmountFormatted,
        breakdown: breakdown.map((row) => ({
          ...row,
          share: totalAmount === ZERO ? ZERO : row.totalAmount / totalAmount,
        })),
      };
    },
  });
}
